import React, { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import {
    Box, Typography, List, ListItemButton, ListItemText, ListSubheader,
    IconButton, Tooltip, Slider
} from "@mui/material";
import { FaBookOpen } from "react-icons/fa";
import { convertFromRaw, EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import MathJax from '../component/react-mathjax-preview';
import { GoSidebarExpand, GoSidebarCollapse } from "react-icons/go";
import { FiMinus, FiSquare, FiCopy, FiX } from "react-icons/fi";
import { BiExpandAlt } from "react-icons/bi";
import "./SFExplanationPreview.css";

const COLOR = "#dfe3e5";

let options = {
    blockStyleFn: (block) => {
        let data = block.getData();
        if (data.size == 0) return;

        let style = {};
        if (data.get('text-align')) style = { ...style, textAlign: data.get('text-align') }
        return {
            attributes: {
                dir: 'auto'
            },
            style
        };
    },
    entityStyleFn: (entity) => {
        const entityType = entity.get('type').toLowerCase();

        if (entityType === 'image') {
            const data = entity.getData();
            let computedWidth = data.width;
            if (computedWidth === undefined)
                computedWidth = 30;

            const float = data.alignment || data.float || data.dataVal;

            let style = {}
            let className = ""

            if (float === 'center') {
                className = "custom-image-align-center"
                style = {
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                }
            } else {
                className = `custom-image-align-${float}`
                if (float) {
                    style = {
                        float: float
                    }
                }
            }

            return {
                element: 'img',
                attributes: {
                    src: data.src,
                    className: className,
                },
                style: {
                    width: computedWidth + '%',
                    ...style,
                },
            };
        }
        if (entityType === 'draft-js-video-plugin-video') {
            const data = entity.getData();
            let computedWidth = data.width;
            if (computedWidth === undefined)
                computedWidth = 30;
            return {
                element: 'iframe',
                attributes: {
                    src: data.src,
                    width: 420,
                    height: 315,
                    allowfullscreen: '',
                }
            };
        }
    },
};

const mainWindowWidth = 900;
const mainWindowHeight = 650;

const SFExplanationPreview = ({ visible, explanations = [], selectedRowIndex = 0, closeLivePreview }) => {
    const nodeRef = useRef(null);
    const closeButtonRef = useRef(null);
    const restoreButtonRef = useRef(null);

    const [zoom, setZoom] = useState(100);
    const [minimized, setMinimized] = useState(false);
    const [maximized, setMaximized] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
    const [selectedExplanation, setSelectedExplanation] = useState(null);
    const [windowSize, setWindowSize] = useState({ width: mainWindowWidth, height: mainWindowHeight });

    useEffect(() => {
        const x = (window.innerWidth - mainWindowWidth) / 2;
        const y = (window.innerHeight - mainWindowHeight) / 2;
        setPosition({ x, y });

        if (explanations && explanations.length > 0) {
            const idx = selectedRowIndex != null && selectedRowIndex < explanations.length ? selectedRowIndex : 0;
            setSelectedExplanation(explanations[idx]);
        }

        const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0);
        return () => window.clearTimeout(focusTimer);
    }, []);

    useEffect(() => {
        if (minimized) {
            window.setTimeout(() => restoreButtonRef.current?.focus(), 0);
        }
        const x = (window.innerWidth - mainWindowWidth) / 2;
        const y = (window.innerHeight - mainWindowHeight) / 2;
        setPosition({ x, y });
    }, [minimized]);

    const closeDialog = () => {
        closeLivePreview();
    };

    const handleDialogKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeDialog();
            return;
        }

        if (event.key !== 'Tab' || !nodeRef.current) return;

        const focusableElements = Array.from(nodeRef.current.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter((element) => element.offsetParent !== null);

        if (!focusableElements.length) {
            event.preventDefault();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    };

    const handleResizeMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = nodeRef.current.offsetWidth;
        const startHeight = nodeRef.current.offsetHeight;

        const onMouseMove = (moveEvent) => {
            const newWidth = Math.max(400, startWidth + (moveEvent.clientX - startX));
            const newHeight = Math.max(300, startHeight + (moveEvent.clientY - startY));
            setWindowSize({ width: newWidth, height: newHeight });
        };

        const onMouseUp = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const convertToHTML = (exp) => {
        let retVal = "";
        try {
            if (exp.text === null || exp.text === undefined || exp.text === "") return retVal;
            const contentState = convertFromRaw(JSON.parse(exp.text));
            const editorState = EditorState.createWithContent(contentState);
            retVal = stateToHTML(editorState.getCurrentContent(), options);

            if (exp.alt_explanation_text !== undefined && exp.alt_explanation_text !== null && exp.alt_explanation_text !== "") {
                var tempDisplayExplanationText = retVal;
                var altExplanationText = exp.alt_explanation_text;
                altExplanationText = altExplanationText.replace(/\n/ig, ' ');
                var altExplanationTextCount = altExplanationText.split(';').length;
                var ExplanationImgTagCount = tempDisplayExplanationText.split('<img').length - 1;
                if ((altExplanationTextCount / 2) == ExplanationImgTagCount) {
                    if (ExplanationImgTagCount >= 1) {
                        var altExplanationTextArr = altExplanationText.split(';');
                        var lastPosOfImg = 0;
                        for (var j = 0; j <= altExplanationTextArr.length - 1; j = j + 2) {
                            lastPosOfImg = tempDisplayExplanationText.indexOf("<img", lastPosOfImg);
                            lastPosOfImg = lastPosOfImg + 4;
                            var b = "  title =\"" + altExplanationTextArr[j] + "\"  alt =\"" + altExplanationTextArr[j + 1] + "\" ";
                            var output = [tempDisplayExplanationText.slice(0, lastPosOfImg), b, tempDisplayExplanationText.slice(lastPosOfImg)].join('');
                            tempDisplayExplanationText = output;
                        }
                    }
                }
                retVal = tempDisplayExplanationText;
            }
        }
        catch (err) {
            console.error(err.message);
        }
        return retVal;
    };

    const getExplanationKey = (e) =>
        `${e.subject_id}_${e.topic_id}_${e.category_id}_${e.subcategory_id}_${e.explanation_id}`;

    if (!visible) return null;

    return (
        <>
            {/* Minimized Floating Button */}
            {minimized && (
                <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 999999 }}>
                    <Tooltip title="Restore Explanation Preview">
                        <IconButton
                            ref={restoreButtonRef}
                            aria-label="Restore Explanation Preview"
                            onClick={() => setMinimized(false)}
                            sx={{
                                backgroundColor: "#bb0000",
                                color: 'white',
                                width: 56,
                                height: 56,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                '&:hover': { backgroundColor: '#e40606' },
                            }}
                        >
                            <FaBookOpen size={24} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {maximized && !minimized ? <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 99,
                    width: '100%',
                    height: "100%",
                    background: "black",
                    opacity: 0.5
                }}
            ></div> : null}

            {/* Main Window */}
            <Draggable
                nodeRef={nodeRef}
                handle=".window-header"
                position={maximized ? { x: 0, y: 0 } : position}
                onDrag={(e, d) => !maximized && setPosition({ x: d.x, y: d.y })}
            >
                <Box
                    ref={nodeRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="sf-explanation-preview-title"
                    onKeyDown={handleDialogKeyDown}
                    sx={{
                        position: "fixed",
                        top: maximized ? "2vh" : 0,
                        left: maximized ? "2vw" : 0,
                        width: maximized ? "95vw" : windowSize.width,
                        height: maximized ? "95vh" : windowSize.height,
                        zIndex: 999999,
                        background: "#dfe3e5",
                        boxShadow: "0 10px 20px rgba(0,0,0,0.35)",
                        borderRadius: 2,
                        display: minimized ? "none" : "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        minWidth: 400,
                        minHeight: 300,
                    }}
                >
                    {/* HEADER */}
                    <Box
                        className="window-header"
                        sx={{
                            background: COLOR,
                            color: "black",
                            p: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "move",
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1} height={"20px"}>
                            <IconButton
                                aria-label={isSidebarExpanded ? "Collapse explanations list" : "Expand explanations list"}
                                onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
                            >
                                {isSidebarExpanded ? <GoSidebarExpand /> : <GoSidebarCollapse />}
                            </IconButton>
                            <Typography id="sf-explanation-preview-title" fontWeight={600}>Explanation Preview</Typography>
                        </Box>

                        <Box display="flex" gap="6px">
                            <IconButton size="small" aria-label="Minimize" onClick={() => setMinimized(true)}>
                                <FiMinus />
                            </IconButton>
                            <IconButton
                                size="small"
                                aria-label={maximized ? "Restore window" : "Maximize window"}
                                onClick={() => setMaximized(!maximized)}
                            >
                                {maximized ? <FiCopy style={{ transform: "scaleX(-1)" }} /> : <FiSquare />}
                            </IconButton>
                            <IconButton ref={closeButtonRef} size="small" aria-label="Close" onClick={closeDialog}>
                                <FiX />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* BODY */}
                    <Box sx={{
                        flex: 1,
                        display: "flex",
                        overflow: "hidden",
                        backgroundColor: "#f1f3f4",
                    }}>
                        {/* LEFT SIDEBAR */}
                        <Box
                            sx={{
                                width: isSidebarExpanded ? 220 : 0,
                                borderRight: "1px solid #ddd",
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                background: '#fff',
                                flexShrink: 0,
                                overflow: "hidden",
                                transition: "width 0.2s ease",
                            }}
                        >
                            <Box sx={{ flex: 1, overflow: "auto" }}>
                                <List subheader={
                                    <ListSubheader
                                        sx={{
                                            fontWeight: 700,
                                            backgroundColor: "#eff1f2",
                                            color: '#333',
                                            lineHeight: '40px',
                                            borderBottom: "2px solid #cfd4d8"
                                        }}
                                    >
                                        EXPLANATIONS ({explanations?.length ?? 0})
                                    </ListSubheader>
                                }>
                                    {explanations && explanations.map((e, i) => {
                                        const isSelected = selectedExplanation &&
                                            getExplanationKey(e) === getExplanationKey(selectedExplanation);

                                        return (
                                            <ListItemButton
                                                key={i}
                                                aria-label={`Explanation: ${e.explanation_name}${isSelected ? ', selected' : ''}`}
                                                onClick={() => setSelectedExplanation(e)}
                                                sx={{
                                                    minHeight: 35,
                                                    backgroundColor: isSelected ? "#f5f5f5" : "transparent",
                                                    borderLeft: isSelected ? `4px solid ${COLOR}` : "4px solid transparent",
                                                }}
                                            >
                                                <ListItemText primary={e.explanation_name} />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Box>
                        </Box>

                        {/* RIGHT CONTENT */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: "flex-start",
                            alignItems: "center",
                            width: "100%",
                        }}>
                            {selectedExplanation ?
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: '768px',
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        px: 2,
                                        mt: 1,
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45 }}>
                                        Zoom
                                    </Typography>
                                    <Slider
                                        aria-label="Explanation zoom"
                                        value={zoom}
                                        min={100}
                                        max={150}
                                        onChange={(e, val) => setZoom(val)}
                                    />
                                    <Typography variant="h6" color="primary" sx={{ minWidth: 45, textAlign: 'right' }}>
                                        {zoom}%
                                    </Typography>
                                </Box>
                                : null}

                            <div style={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center", padding: "24px", width: "100%" }}>
                                <div style={{
                                    width: "100%",
                                    maxWidth: 1200 * (zoom / 100),
                                    background: "#fff",
                                    border: "1px solid #c7c7c7",
                                    padding: "32px",
                                    height: "100%",
                                    position: 'relative',
                                    overflow: "hidden",
                                }}>
                                    {selectedExplanation && (
                                        <div
                                            role="region"
                                            aria-label={`Explanation: ${selectedExplanation.explanation_name}`}
                                            className="zoom-container"
                                            style={{
                                                transform: `scale(${zoom / 100})`,
                                                transformOrigin: "top left",
                                                transition: "transform 0.15s ease-out",
                                                width: `${(100 / zoom) * 100}%`,
                                                height: `${(100 / zoom) * 100}%`,
                                                position: 'relative',
                                                overflow: 'auto',
                                            }}
                                        >
                                            <MathJax math={convertToHTML(selectedExplanation)} />
                                        </div>
                                    )}

                                    {!selectedExplanation && (
                                        <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                                            <Box textAlign="center">
                                                <FaBookOpen aria-hidden="true" size={60} color={COLOR} />
                                                <Typography variant="h6" mt={2}>Select an Explanation</Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Click an item from the left panel
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Box>

                    {/* Resize grip handle */}
                    {!maximized && (
                        <Box
                            onMouseDown={handleResizeMouseDown}
                            sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                color: "#666",
                                cursor: "nwse-resize",
                                lineHeight: 0,
                                padding: "4px",
                                '&:hover': { color: '#bb0000' },
                            }}
                        >
                            <BiExpandAlt size={16} />
                        </Box>
                    )}
                </Box>
            </Draggable>
        </>
    );
};

export default SFExplanationPreview;
